<template>
  <div class="school-form-page">

    <!-- Back Button -->
    <app-back-button
      i18nCode="common.back.default" 
    />

    <!-- Title  -->
    <h1 class="app-content-title app-content-title--hidden">
      <app-i18n code="entities.school.edit.title" v-if="isEditing"></app-i18n>
      <app-i18n code="entities.school.new.title" v-if="!isEditing"></app-i18n>
    </h1>

    <!-- Content -->
    <div class="app-content-page">
      <div class="app-page-spinner" v-if="findLoading" v-loading="findLoading"></div>

      <el-form
        v-loading="saveLoading"
        v-if="model"
        :label-position="labelPosition"
        :label-width="labelWidthForm"
        :model="model"
        :rules="rules"
        @submit.native.prevent="doSubmit"
        class="form"
        ref="form"
      >
      <!-- Subtitle -->
      <h2 class="app-content-subtitle">
        <app-i18n
          code="entities.school.form.subtitle.information"
        />
      </h2>

      <el-row
        v-if="model"
        :gutter="20"
        class="school-form-page__row"
      >
        <!-- Name -->
        <el-col
          :lg="11"
          :md="16"
          :sm="24"
          class="school-form-page__row__col"
        >
          <el-form-item
            :label="fields.name.label"
            :prop="fields.name.name"
            class="school-form-page__row__col__item"
          >
            <!-- Input -->
            <el-input
              v-if="isEditing"
              v-model="model[fields.name.name]"
              prefix-icon="icon-home"
              :placeholder="
                i18n('entities.school.placeholders.name')
              "
            />
          </el-form-item>
        </el-col>

        <!-- Location -->
        <el-col :lg="11" :md="16" :sm="24">
          <el-form-item
            :label="fields.location.label"
            :prop="fields.location.name"
          >
            <app-location-input
              :key="model.id"
              :disabled="disabledLocation"
              :placeholder="
                i18n('entities.school.placeholders.location')
              "
              v-model="model[fields.location.name]"
            />
          </el-form-item>
        </el-col>
      </el-row>

      <!-- Separator -->
      <div
        v-if="!existSchool || isEditing"
        class="app-content-separator"
      />

      <!-- Subtitle -->
      <h2 v-if="!existSchool || isEditing" class="app-content-subtitle">
        <app-i18n
          code="entities.school.form.subtitle.management"
        />
      </h2>

      <!-- Description -->
      <h3 v-if="!existSchool || isEditing" class="app-content-description">
        <app-i18n
          code="entities.school.form.description.email"
        />
      </h3>

      <el-row v-if="model" :gutter="10">
        <!-- Emails -->
        <el-col :lg="24" :md="24" :sm="24">
          <el-form-item
            v-if="!existSchool || isEditing"
            :label="fields.emails.label"
            :prop="fields.emails.name"
            :required="fields.emails.required"
          >
            <emails-input
              v-model="model[fields.emails.name]"
              :key="model.id"
              :disabled="disabledLocation"
              prefix-icon="icon-mail"
              :placeholder="
                i18n('entities.school.placeholders.email')
              "
            />
          </el-form-item>
        </el-col>
      </el-row>

      <!-- Form Controls -->
      <el-form-item class="school-form-page__controls">
        <div class="form-buttons">
          <el-button type="secondary" @click="doBack">
            <app-i18n code="common.back.default" />
          </el-button>

          <el-button
            class="right"
            :disabled="saveLoading"
            @click="doSubmit"
            type="primary"
          >
            <app-i18n v-if="!isEditing" code="common.match" />
            <app-i18n v-if="isEditing" code="common.save" />
          </el-button>
        </div>
      </el-form-item>
      
      </el-form>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import { FormSchema } from '@shared/form/form-schema';
import { SchoolModel } from '@modules/school/school-model';
import { get } from 'lodash';
import { i18n } from '@shared/i18n';

const { fields } = SchoolModel;
const formSchema = new FormSchema([
  fields.id,
  fields.name,
	fields.location,
	fields.emails,
]);

export default {
  name: 'app-school-form-page',

  props: {
    id: String,
  },

  data() {
    return {
      rules: formSchema.rules(),
      model: null,
    };
  },

  computed: {
    ...mapGetters({
      labelPosition: 'layout/labelPosition',
      labelWidthForm: 'layout/labelWidthForm',
      record: 'school/form/record',
      findLoading: 'school/form/findLoading',
      saveLoading: 'school/form/saveLoading',
      currentManage: 'auth/manage/currentManage',
    }),

    propertyId() {
			return get(
				this,
				'currentManage.property.id',
				null,
			);
    },

    subpropertyId() {
			return get(
				this,
				'currentManage.subproperty.id',
				null,
			);
    },
    
    existSchool() {
			return (
				this.model &&
				this.model.id &&
				this.model.id !== null
			);
		},

    isEditing() {
      return !!this.id;
    },

    fields() {
      return fields;
    },

    disabledLocation() {
			return !( !this.existSchool || this.isEditing )
		},
  },

  async mounted() {
    if (this.isEditing) {
      await this.doFind({
          id: this.id,
          subpropertyId: this.subpropertyId,
      });

    } else {
      await this.doNew();
    }
    this.model = formSchema.initialValues(this.record);
  },

  methods: {
    ...mapActions({
      doFind: 'school/form/doFind',
      doNew: 'school/form/doNew',
      doUpdate: 'school/form/doUpdate',
      doCreate: 'school/form/doCreate',
    }),

    doReset() {
      this.model = formSchema.initialValues(this.record);
    },

    async doSubmit() {
      try {
        await this.$refs.form.validate();
      } catch (error) {
        return;
      }

      const { id, ...values } = formSchema.cast(this.model);

      if (this.isEditing) {
        await this.doUpdate({ 
          id: id,
          data: values, 
          manage: { 
            subpropertyId: this.subpropertyId, 
            propertyId: this.propertyId, 
            type: 'subporperty'
          },
        });
      } else {
        //TODO: Function to create one school
        console.log("Function to create one school");
        //return this.doCreate(values);
      }
    },

    i18n(param) {
			return i18n(param);
    },
    
    doBack() {
      return this.$router.go(-1);
    }, 
  },
};
</script>

<style lang="scss" scoped>
.school-form-page {
	&__row {
    padding-top: 32px;
		&__col {
			&__item {
				&__select {
					&__option {
						padding: 0 0 0 0;
						padding-left: 40px;
						&__content {
							color: #6b6d72;
							font-size: 16px;
						}
					}
					&__empty {
						display: grid;
						grid-template-columns: 24px auto;
						grid-template-rows: auto;
						grid-template-areas: 'icon text';
						color: #6b6d72;
						background: #f8f8f8;
						padding: 16px;
						padding-left: 40px;
						cursor: pointer;

						&__icon {
							grid-area: icon;
							color: #c5c5c5;
						}
						&__text {
							grid-area: text;
							font-size: 14px;
						}
					}
				}
			}
		}
	}
	&__controls {
		padding-top: 32px;
  }
}
.app-content-separator {
  margin: 25px 0 40px 0;  
}
.app-content-subtitle {
  margin: 0px;
  margin-top: 30px;
}
.app-content-description {
  margin-top: 16px;
  margin-bottom: 30px;
}
</style>
